/**
 * Sidebar Component
 * 
 * Navigation sidebar for tablet and desktop breakpoints.
 * - Tablet portrait: Collapsed (64px, icons only)
 * - Tablet landscape: Expanded (200px, icons + labels)
 * - Desktop: Always expanded (240px)
 * 
 * @see docs/design/02_TABLET_SPEC.md
 * @see docs/design/03_DESKTOP_SPEC.md
 */

import React from 'react';
import { View, Pressable, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyles } from '../../../hooks/useStyles';
import { NAVIGATION, Z_INDEX } from '../../../shared/tokens/layout';
import { addColorOpacity } from '../../../shared/utils/styles';
import { NavItem, PRIMARY_NAV_ITEMS, SECONDARY_NAV_ITEMS, filterNavItems } from './NavConfig';

// =============================================================================
// TYPES
// =============================================================================

export interface SidebarProps {
  /** Currently active nav item */
  activeItemId: string;
  /** Callback when nav item is pressed */
  onItemPress: (item: NavItem) => void;
  /** Whether sidebar is expanded (shows labels) */
  expanded: boolean;
  /** Width of the sidebar */
  width: number;
  /** Whether current user is admin */
  isAdmin?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function Sidebar({
  activeItemId,
  onItemPress,
  expanded,
  width,
  isAdmin = false,
}: SidebarProps) {
  const insets = useSafeAreaInsets();
  const { tokens } = useStyles();

  const primaryItems = filterNavItems(PRIMARY_NAV_ITEMS, isAdmin);
  const secondaryItems = filterNavItems(SECONDARY_NAV_ITEMS, isAdmin);

  return (
    <View
      style={[
        styles.container,
        {
          width,
          paddingTop: insets.top + 16,
          paddingBottom: insets.bottom + 16,
          backgroundColor: tokens.color.surface,
          borderRightColor: tokens.color.border,
        },
      ]}
    >
      {/* Logo / App Name */}
      {expanded && (
        <View style={styles.logoContainer}>
          <Text style={[styles.logoText, { color: tokens.color.primary }]}>
            🌿 FarmerJohn
          </Text>
        </View>
      )}
      {!expanded && (
        <View style={styles.logoContainerCollapsed}>
          <Text style={styles.logoEmoji}>🌿</Text>
        </View>
      )}

      {/* Primary Nav Items */}
      <View style={styles.navSection}>
        {primaryItems.map((item) => (
          <NavItemButton
            key={item.id}
            item={item}
            isActive={activeItemId === item.id}
            expanded={expanded}
            onPress={() => onItemPress(item)}
            tokens={tokens}
          />
        ))}
      </View>

      {/* Divider */}
      <View style={[styles.divider, { backgroundColor: tokens.color.border }]} />

      {/* Secondary Nav Items */}
      <View style={styles.navSection}>
        {secondaryItems.map((item) => (
          <NavItemButton
            key={item.id}
            item={item}
            isActive={activeItemId === item.id}
            expanded={expanded}
            onPress={() => onItemPress(item)}
            tokens={tokens}
          />
        ))}
      </View>

      {/* Spacer */}
      <View style={styles.spacer} />

      {/* Account Section */}
      <View style={styles.accountSection}>
        <AccountButton expanded={expanded} tokens={tokens} />
      </View>
    </View>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface NavItemButtonProps {
  item: NavItem;
  isActive: boolean;
  expanded: boolean;
  onPress: () => void;
  tokens: any;
}

function NavItemButton({ item, isActive, expanded, onPress, tokens }: NavItemButtonProps) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.navItem,
        expanded ? styles.navItemExpanded : styles.navItemCollapsed,
        isActive && { backgroundColor: addColorOpacity(tokens.color.primary, 0.1) },
        pressed && { opacity: 0.7 },
      ]}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      accessibilityLabel={item.label}
    >
      {/* Active indicator bar */}
      {isActive && (
        <View style={[styles.activeIndicator, { backgroundColor: tokens.color.primary }]} />
      )}
      
      <Ionicons
        name={isActive ? item.iconActive : item.icon}
        size={22}
        color={isActive ? tokens.color.primary : addColorOpacity(tokens.color.onSurface, 0.7)}
      />
      
      {expanded && (
        <Text
          style={[
            styles.navItemLabel,
            {
              color: isActive ? tokens.color.primary : tokens.color.onSurface,
              fontWeight: isActive ? '600' : '400',
            },
          ]}
          numberOfLines={1}
        >
          {item.label}
        </Text>
      )}
    </Pressable>
  );
}

interface AccountButtonProps {
  expanded: boolean;
  tokens: any;
}

function AccountButton({ expanded, tokens }: AccountButtonProps) {
  // TODO: Get actual user data from auth store
  const userName = 'Collin';
  const userEmail = 'collin@...';

  return (
    <Pressable
      style={({ pressed }) => [
        styles.accountButton,
        expanded ? styles.accountButtonExpanded : styles.accountButtonCollapsed,
        pressed && { opacity: 0.7 },
      ]}
      accessibilityRole="button"
      accessibilityLabel="Account menu"
    >
      <View style={[styles.avatar, { backgroundColor: tokens.color.primary }]}>
        <Text style={styles.avatarText}>
          {userName.charAt(0).toUpperCase()}
        </Text>
      </View>
      
      {expanded && (
        <View style={styles.accountInfo}>
          <Text style={[styles.accountName, { color: tokens.color.onSurface }]} numberOfLines={1}>
            {userName}
          </Text>
          <Text style={[styles.accountEmail, { color: addColorOpacity(tokens.color.onSurface, 0.6) }]} numberOfLines={1}>
            {userEmail}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  container: {
    borderRightWidth: 1,
    zIndex: Z_INDEX.navigation,
  },
  logoContainer: {
    paddingHorizontal: 16,
    paddingBottom: 24,
  },
  logoContainerCollapsed: {
    alignItems: 'center',
    paddingBottom: 24,
  },
  logoText: {
    fontSize: 18,
    fontWeight: '700',
  },
  logoEmoji: {
    fontSize: 24,
  },
  navSection: {
    gap: 4,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    position: 'relative',
  },
  navItemExpanded: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginHorizontal: 8,
  },
  navItemCollapsed: {
    paddingVertical: 12,
    justifyContent: 'center',
    marginHorizontal: 8,
  },
  activeIndicator: {
    position: 'absolute',
    left: 0,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 2,
  },
  navItemLabel: {
    marginLeft: 12,
    fontSize: 14,
  },
  divider: {
    height: 1,
    marginVertical: 16,
    marginHorizontal: 16,
  },
  spacer: {
    flex: 1,
  },
  accountSection: {
    borderTopWidth: 1,
    borderTopColor: 'transparent', // Will be set via tokens
    paddingTop: 16,
  },
  accountButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
  },
  accountButtonExpanded: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginHorizontal: 8,
  },
  accountButtonCollapsed: {
    justifyContent: 'center',
    paddingVertical: 8,
    marginHorizontal: 8,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  accountInfo: {
    marginLeft: 12,
    flex: 1,
  },
  accountName: {
    fontSize: 14,
    fontWeight: '500',
  },
  accountEmail: {
    fontSize: 12,
  },
});

export default Sidebar;

